import { Component } from '@theme/component';

/**
 * A minimal, dependency-free horizontal scroller for the Quick Links carousel.
 *
 * Deliberately does NOT use the shared `slideshow-component` (assets/slideshow.js).
 * That component attaches `mouseenter`/`pointerenter` listeners to itself (to pause
 * autoplay and reveal arrows on hover) as well as a `mousedown` drag handler on its
 * scroller. On iOS Safari, an ancestor carrying hover-type listeners makes the first
 * tap on a descendant link only simulate ':hover' - the actual click/navigation only
 * fires on a second tap. Quick Links doesn't need autoplay, dragging, or looping, so
 * it uses plain native scroll-snap scrolling plus click-to-scroll arrow buttons,
 * which have no hover listeners at all and don't trigger that quirk.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} scroller
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class QuickLinksCarousel extends Component {
  requiredRefs = ['scroller'];

  connectedCallback() {
    super.connectedCallback();

    const { scroller } = this.refs;
    scroller.addEventListener('scroll', this.#updateArrowState, { passive: true });
    this.#updateArrowState();
  }

  /**
   * Scrolls to the previous page of links.
   * @param {Event} [event]
   */
  previous = (event) => {
    event?.preventDefault();
    this.#scrollByPage(-1);
  };

  /**
   * Scrolls to the next page of links.
   * @param {Event} [event]
   */
  next = (event) => {
    event?.preventDefault();
    this.#scrollByPage(1);
  };

  /**
   * @param {1 | -1} direction
   */
  #scrollByPage(direction) {
    const { scroller } = this.refs;
    scroller.scrollBy({ left: scroller.clientWidth * 0.8 * direction, behavior: 'smooth' });
  }

  #updateArrowState = () => {
    const { scroller, previous, next } = this.refs;
    const maxScrollLeft = scroller.scrollWidth - scroller.clientWidth;

    if (previous) previous.disabled = scroller.scrollLeft <= 1;
    if (next) next.disabled = scroller.scrollLeft >= maxScrollLeft - 1;
  };
}

if (!customElements.get('quick-links-carousel')) {
  customElements.define('quick-links-carousel', QuickLinksCarousel);
}
